import React, {useEffect, useState} from 'react';
import {useDebounce} from '../../hooks/useDebunce';
import {useQuery} from '@tanstack/react-query';
import QUERY_KEYS from '../../constants/queryKeys';
import {handleGetAllUserGroupsService} from '../../services/userGroups';
import {GroupsListResponse} from '../../pages/users/components/GroupScreens/groups';
import {ApiResponse} from '../../services/axiosInstance';
import {useClientsContext, getLocationFilterParams} from '../clients/ClientsContextProvider';

interface GroupsContextType {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  searchText: string;
  setSearchText: React.Dispatch<React.SetStateAction<string>>;
  selectedGroups: Array<string | number>;
  setSelectedGroups: React.Dispatch<
    React.SetStateAction<Array<string | number>>
  >;
  selectedGroup: any;
  setSelectedGroup: React.Dispatch<React.SetStateAction<any>>;
  showAddEditDialog: boolean;
  setShowAddEditDialog: React.Dispatch<React.SetStateAction<boolean>>;

  allUserGroupsData: GroupsListResponse | undefined;
  refetchAllUserGroups: () => void;
}

const GroupsContext = React.createContext<GroupsContextType | null>(null);

type Props = {
  children: React.ReactNode;
};

function GroupsContextProvider({children}: Props) {
  const {selectedLocation} = useClientsContext();

  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useState('');
  const [selectedGroups, setSelectedGroups] = useState<Array<string | number>>(
    [],
  );
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [showAddEditDialog, setShowAddEditDialog] = useState(false);

  const debouncedSearch = useDebounce(searchText, 500);

  const {data: allUserGroupsData, refetch: refetchAllUserGroups} = useQuery<
    ApiResponse<GroupsListResponse>,
    Error,
    GroupsListResponse
  >({
    queryKey: [
      QUERY_KEYS.GET_ALL_USER_GROUPS,
      page,
      selectedLocation?.id,
      debouncedSearch,
    ],
    queryFn: () =>
      handleGetAllUserGroupsService({
        page: page,
        limit: 50,
        search: debouncedSearch,
        sortBy: 'isActive',
        ...getLocationFilterParams(selectedLocation),
      }),
    enabled: !!selectedLocation,
    select: resp => resp.data,
  });

  useEffect(() => {
    setPage(1);
    setSelectedGroups([]);
  }, [selectedLocation]);

  return (
    <GroupsContext.Provider
      value={{
        page,
        setPage,
        searchText,
        setSearchText,
        selectedGroups,
        setSelectedGroups,
        selectedGroup,
        setSelectedGroup,
        showAddEditDialog,
        setShowAddEditDialog,

        allUserGroupsData,
        refetchAllUserGroups,
      }}>
      {children}
    </GroupsContext.Provider>
  );
}

export default GroupsContextProvider;

export const useGroupsContext = () => {
  const context = React.useContext(GroupsContext);
  if (context === null) {
    throw new Error(
      'useGroupsContext must be used within an GroupsContextProvider',
    );
  }
  return context;
};
